/**
 * Mantenimiento: reparar semáforos, cerrar métricas y respaldar Firestore.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ink, studio, Field, FilterDate, cols } from './studioUi';

function formatBackupLabel(folder) {
    if (!folder) return '';
    const datePart = folder.split('_')[0];
    const parsed = new Date(datePart);
    if (Number.isNaN(parsed.getTime())) return folder;
    return `${parsed.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}  ·  ${folder}`;
}

const MaintenancePanel = ({
    fetchWithAuth,
    API_URL_INTERNAL,
    users,
    dashboards = [],
    cargarTodo,
    userRole,
    userPermissions,
}) => {
    const [busy, setBusy] = useState('');
    const [notice, setNotice] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [typedConfirm, setTypedConfirm] = useState('');

    const [viewReportId, setViewReportId] = useState('');
    const [viewStart, setViewStart] = useState('');
    const [viewEnd, setViewEnd] = useState('');
    const [viewImpact, setViewImpact] = useState(null);

    const [loginUserId, setLoginUserId] = useState('');
    const [loginStart, setLoginStart] = useState('');
    const [loginEnd, setLoginEnd] = useState('');
    const [loginImpact, setLoginImpact] = useState(null);

    const [metricUserId, setMetricUserId] = useState('');
    const [metricImpact, setMetricImpact] = useState(null);

    const [approvalImpact, setApprovalImpact] = useState(null);

    const [resetReportId, setResetReportId] = useState('');
    const [resetOpts, setResetOpts] = useState({
        clearDeadline: true,
        clearLock: true,
        unapproveResponsibles: true,
        unlockComments: false,
    });

    const [backups, setBackups] = useState([]);
    const [selectedBackup, setSelectedBackup] = useState('');
    const [backupError, setBackupError] = useState('');
    const [canRestore, setCanRestore] = useState(
        userRole === 'admin' || userPermissions?.maintenance_restore === true,
    );

    const reportOptions = useMemo(
        () => [...(dashboards || [])].sort((a, b) => String(a.reporteNombre || '').localeCompare(String(b.reporteNombre || ''), 'es')),
        [dashboards],
    );

    const userOptions = useMemo(
        () => [...(users || [])].sort((a, b) => String(a.nombre || a.email || '').localeCompare(String(b.nombre || b.email || ''), 'es')),
        [users],
    );

    const showNotice = (type, text) => setNotice({ type, text });

    const run = async (key, fn) => {
        setBusy(key);
        try {
            await fn();
        } catch (error) {
            showNotice('error', error.message || 'No se pudo completar la acción.');
        } finally {
            setBusy('');
        }
    };

    const api = async (path, options = {}) => {
        const res = await fetchWithAuth(`${API_URL_INTERNAL}${path}`, options);
        const data = await res.json().catch(() => ({}));
        if (!data.success && data.error) throw new Error(data.error);
        return data;
    };

    const loadImpact = useCallback(async (type, params, setter) => {
        try {
            const query = new URLSearchParams({ type, ...params });
            Object.keys(params).forEach((key) => {
                if (!params[key]) query.delete(key);
            });
            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/maintenance/impact?${query.toString()}`);
            const data = await res.json();
            if (data.success) setter(data);
            else setter({ error: true });
        } catch (error) {
            setter({ error: true });
        }
    }, [API_URL_INTERNAL, fetchWithAuth]);

    useEffect(() => {
        const t = setTimeout(() => {
            loadImpact('views', { reportId: viewReportId, startDate: viewStart, endDate: viewEnd }, setViewImpact);
        }, 250);
        return () => clearTimeout(t);
    }, [viewReportId, viewStart, viewEnd, loadImpact]);

    useEffect(() => {
        const t = setTimeout(() => {
            loadImpact('logins', { userId: loginUserId, startDate: loginStart, endDate: loginEnd }, setLoginImpact);
        }, 250);
        return () => clearTimeout(t);
    }, [loginUserId, loginStart, loginEnd, loadImpact]);

    useEffect(() => {
        const t = setTimeout(() => {
            loadImpact('metrics', { userId: metricUserId }, setMetricImpact);
        }, 250);
        return () => clearTimeout(t);
    }, [metricUserId, loadImpact]);

    useEffect(() => {
        loadImpact('approvals', {}, setApprovalImpact);
    }, [loadImpact]);

    const loadBackups = async () => {
        setBackupError('');
        const data = await api('/admin/maintenance/backups');
        setBackups(data.backups || []);
        if (typeof data.canRestore === 'boolean') setCanRestore(data.canRestore);
        if (!(data.backups || []).includes(selectedBackup)) setSelectedBackup('');
        return data.backups || [];
    };

    useEffect(() => {
        loadBackups().catch((error) => setBackupError(error.message));
        // Lista al entrar; no re-disparar por selectedBackup.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openConfirm = (config) => {
        setTypedConfirm('');
        setConfirm(config);
    };

    const closeConfirm = () => {
        setConfirm(null);
        setTypedConfirm('');
    };

    const handleConfirm = async () => {
        if (!confirm) return;
        if (confirm.requireWord && typedConfirm !== confirm.requireWord) return;
        const action = confirm.action;
        closeConfirm();
        await run(confirm.busyKey, action);
    };

    const afterDataChange = async () => {
        if (typeof cargarTodo === 'function') cargarTodo();
        loadImpact('views', { reportId: viewReportId, startDate: viewStart, endDate: viewEnd }, setViewImpact);
        loadImpact('logins', { userId: loginUserId, startDate: loginStart, endDate: loginEnd }, setLoginImpact);
        loadImpact('metrics', { userId: metricUserId }, setMetricImpact);
        loadImpact('approvals', {}, setApprovalImpact);
    };

    const disabled = Boolean(busy);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {notice && (
                <div style={{
                    ...studio.card,
                    backgroundColor: notice.type === 'error' ? ink.dangerSoft : '#e6f4ea',
                    borderColor: notice.type === 'error' ? '#f6aea9' : '#a8dab5',
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                        <span style={{ color: ink.text }}>{notice.text}</span>
                        <button type="button" onClick={() => setNotice(null)} style={studio.ghostBtn}>Cerrar</button>
                    </div>
                </div>
            )}

            <Section title="Reparar datos" hint="No borra historial. Recalcula o limpia el estado de un reporte.">
                <ActionCard
                    title="Recalcular semáforo de aprobación"
                    description="Recorre responsables y escribe approvedCount, totalResponsibles y approvalStatus (pending, complete, complete_late u overdue). No envía correos ni toca el lock."
                >
                    <ImpactLine>
                        {approvalImpact?.error
                            ? 'No se pudo calcular el impacto'
                            : approvalImpact
                                ? `${approvalImpact.reports} reportes maestros`
                                : 'Calculando impacto…'}
                    </ImpactLine>
                    <button
                        type="button"
                        style={studio.primaryBtn}
                        disabled={disabled}
                        onClick={() => openConfirm({
                            title: 'Recalcular semáforo',
                            body: `Se actualizarán ${approvalImpact?.reports ?? 'todos los'} reportes. Es seguro y no borra aprobaciones.`,
                            confirmLabel: 'Recalcular',
                            busyKey: 'approvals',
                            action: async () => {
                                const data = await api('/admin/maintenance/recalculate-approvals', { method: 'POST' });
                                showNotice('ok', data.message);
                                await afterDataChange();
                            },
                        })}
                    >
                        {busy === 'approvals' ? 'Recalculando…' : 'Recalcular aprobaciones'}
                    </button>
                </ActionCard>

                <ActionCard
                    title="Resetear un reporte"
                    description="Quita deadline y freeze, y por defecto destilda a los responsables para que el semáforo vuelva a pendiente."
                >
                    <select
                        value={resetReportId}
                        onChange={(e) => setResetReportId(e.target.value)}
                        style={{ ...studio.control, marginBottom: '12px' }}
                    >
                        <option value="">— Elige un reporte —</option>
                        {reportOptions.map((report) => (
                            <option key={report.id} value={report.id}>
                                {report.reporteNombre} {report.cliente ? `· ${report.cliente}` : ''}
                            </option>
                        ))}
                    </select>
                    <label style={checkStyle}>
                        <input type="checkbox" checked={resetOpts.clearDeadline} onChange={(e) => setResetOpts((prev) => ({ ...prev, clearDeadline: e.target.checked }))} />
                        Borrar deadline
                    </label>
                    <label style={checkStyle}>
                        <input type="checkbox" checked={resetOpts.clearLock} onChange={(e) => setResetOpts((prev) => ({ ...prev, clearLock: e.target.checked }))} />
                        Quitar bloqueo de aprobación
                    </label>
                    <label style={checkStyle}>
                        <input type="checkbox" checked={resetOpts.unapproveResponsibles} onChange={(e) => setResetOpts((prev) => ({ ...prev, unapproveResponsibles: e.target.checked }))} />
                        Destildar responsables
                    </label>
                    <label style={{ ...checkStyle, marginBottom: '16px' }}>
                        <input type="checkbox" checked={resetOpts.unlockComments} onChange={(e) => setResetOpts((prev) => ({ ...prev, unlockComments: e.target.checked }))} />
                        Reabrir comentarios
                    </label>
                    <button
                        type="button"
                        style={studio.dangerBtn}
                        disabled={disabled || !resetReportId}
                        onClick={() => {
                            const report = reportOptions.find((item) => item.id === resetReportId);
                            openConfirm({
                                title: 'Resetear reporte',
                                body: `«${report?.reporteNombre || resetReportId}» volverá a pendiente según las casillas marcadas. No se puede deshacer.`,
                                confirmLabel: 'Resetear',
                                danger: true,
                                busyKey: 'reset-report',
                                action: async () => {
                                    const data = await api(`/admin/maintenance/reset-report-status/${resetReportId}`, {
                                        method: 'POST',
                                        body: JSON.stringify(resetOpts),
                                    });
                                    showNotice('ok', data.message);
                                    await afterDataChange();
                                },
                            });
                        }}
                    >
                        {busy === 'reset-report' ? 'Reseteando…' : 'Resetear reporte'}
                    </button>
                </ActionCard>
            </Section>

            <Section title="Cerrar periodo" hint="Borra historial de medición. Elige alcance antes de confirmar.">
                <ActionCard
                    title="Vistas de reportes"
                    description="Elimina registros de report_views y alinea el contador de la biblioteca y el home."
                >
                    <select value={viewReportId} onChange={(e) => setViewReportId(e.target.value)} style={{ ...studio.control, marginBottom: '10px' }}>
                        <option value="">Todos los reportes</option>
                        {reportOptions.map((report) => (
                            <option key={report.id} value={report.id}>{report.reporteNombre}</option>
                        ))}
                    </select>
                    <DateRow start={viewStart} end={viewEnd} onStart={setViewStart} onEnd={setViewEnd} />
                    <ImpactLine>
                        {viewImpact?.error
                            ? 'No se pudo calcular el impacto (si filtras reporte y fechas, despliega los índices de Firestore)'
                            : viewImpact
                                ? `${viewImpact.records} registros · ${viewImpact.reports} reportes afectados`
                                : 'Calculando impacto…'}
                    </ImpactLine>
                    <button
                        type="button"
                        style={studio.dangerBtn}
                        disabled={disabled}
                        onClick={() => openConfirm({
                            title: 'Borrar vistas',
                            body: `Se eliminarán ${viewImpact?.records ?? 0} registros de vistas. Esta acción no se deshace.`,
                            confirmLabel: 'Borrar vistas',
                            danger: true,
                            busyKey: 'views',
                            action: async () => {
                                const data = await api('/admin/maintenance/report-views/clear', {
                                    method: 'POST',
                                    body: JSON.stringify({
                                        reportId: viewReportId || null,
                                        startDate: viewStart || null,
                                        endDate: viewEnd || null,
                                    }),
                                });
                                showNotice('ok', data.message);
                                await afterDataChange();
                            },
                        })}
                    >
                        {busy === 'views' ? 'Borrando…' : 'Borrar vistas'}
                    </button>
                </ActionCard>

                <ActionCard
                    title="Inicios de sesión"
                    description="Vacía login_history, que alimenta las estadísticas de usuarios."
                >
                    <select value={loginUserId} onChange={(e) => setLoginUserId(e.target.value)} style={{ ...studio.control, marginBottom: '10px' }}>
                        <option value="">Todos los usuarios</option>
                        {userOptions.map((user) => (
                            <option key={user.uid} value={user.uid}>{user.nombre || user.email} ({user.email})</option>
                        ))}
                    </select>
                    {!userOptions.length && (
                        <p style={{ ...studio.hint, color: '#b06000' }}>
                            No hay usuarios en la lista. Recarga el panel o verifica el permiso de mantenimiento.
                        </p>
                    )}
                    <DateRow start={loginStart} end={loginEnd} onStart={setLoginStart} onEnd={setLoginEnd} />
                    <ImpactLine>
                        {loginImpact?.error
                            ? 'No se pudo calcular el impacto (si filtras usuario y fechas, despliega los índices de Firestore)'
                            : loginImpact
                                ? `${loginImpact.records} inicios de sesión`
                                : 'Calculando impacto…'}
                    </ImpactLine>
                    <button
                        type="button"
                        style={studio.dangerBtn}
                        disabled={disabled}
                        onClick={() => openConfirm({
                            title: 'Borrar inicios de sesión',
                            body: `Se eliminarán ${loginImpact?.records ?? 0} registros. Las gráficas de logins quedarán vacías en ese alcance.`,
                            confirmLabel: 'Borrar logins',
                            danger: true,
                            busyKey: 'logins',
                            action: async () => {
                                const data = await api('/admin/maintenance/user-logins/clear', {
                                    method: 'POST',
                                    body: JSON.stringify({
                                        userId: loginUserId || null,
                                        startDate: loginStart || null,
                                        endDate: loginEnd || null,
                                    }),
                                });
                                showNotice('ok', data.message);
                                await afterDataChange();
                            },
                        })}
                    >
                        {busy === 'logins' ? 'Borrando…' : 'Borrar inicios de sesión'}
                    </button>
                </ActionCard>

                <ActionCard
                    title="Aprobaciones tardías y falsas"
                    description="Pone en cero los contadores y borra también el historial (late_approvals / false_approvals). Sirve para abrir un periodo nuevo de medición."
                >
                    <select value={metricUserId} onChange={(e) => setMetricUserId(e.target.value)} style={{ ...studio.control, marginBottom: '10px' }}>
                        <option value="">Todos los usuarios</option>
                        {userOptions.map((user) => (
                            <option key={user.uid} value={user.uid}>{user.nombre || user.email} ({user.email})</option>
                        ))}
                    </select>
                    <ImpactLine>
                        {metricImpact?.error
                            ? 'No se pudo calcular el impacto'
                            : metricImpact
                                ? (metricUserId
                                    ? `${metricImpact.lateApprovalCount || 0} tardías · ${metricImpact.falseApprovalCount || 0} falsas · ${(metricImpact.lateRecords || 0) + (metricImpact.falseRecords || 0)} filas de historial`
                                    : `${metricImpact.users} usuarios`)
                                : 'Calculando impacto…'}
                    </ImpactLine>
                    <button
                        type="button"
                        style={studio.dangerBtn}
                        disabled={disabled}
                        onClick={() => openConfirm({
                            title: 'Cerrar periodo de métricas',
                            body: metricUserId
                                ? 'Se pondrán en cero los contadores de este usuario y se borrará su historial de tardías y falsas.'
                                : `Se pondrán en cero los contadores de ${metricImpact?.users ?? 'todos los'} usuarios y se borrará el historial asociado.`,
                            confirmLabel: 'Cerrar periodo',
                            danger: true,
                            busyKey: 'metrics',
                            action: async () => {
                                const data = await api('/admin/maintenance/reset-user-metrics', {
                                    method: 'POST',
                                    body: JSON.stringify({ userId: metricUserId || null }),
                                });
                                showNotice('ok', data.message);
                                await afterDataChange();
                            },
                        })}
                    >
                        {busy === 'metrics' ? 'Cerrando…' : 'Cerrar periodo de métricas'}
                    </button>
                </ActionCard>
            </Section>

            <Section title="Respaldo" hint="Export a Cloud Storage. Restaurar sobreescribe toda la base.">
                <ActionCard
                    title="Crear y consultar respaldos"
                    description="El export corre en segundo plano. Los respaldos programados se configuran en Cloud Scheduler contra este mismo endpoint, no con una Cloud Function aparte."
                >
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <button
                            type="button"
                            style={studio.primaryBtn}
                            disabled={disabled}
                            onClick={() => openConfirm({
                                title: 'Iniciar respaldo',
                                body: 'Se lanzará un export completo de Firestore al bucket configurado. Puede tardar varios minutos.',
                                confirmLabel: 'Iniciar respaldo',
                                busyKey: 'backup',
                                action: async () => {
                                    const data = await api('/admin/maintenance/backup', { method: 'POST' });
                                    showNotice('ok', data.message);
                                    setTimeout(() => loadBackups().catch(() => {}), 2000);
                                },
                            })}
                        >
                            {busy === 'backup' ? 'Iniciando…' : 'Crear respaldo ahora'}
                        </button>
                        <button
                            type="button"
                            style={studio.ghostBtn}
                            disabled={disabled}
                            onClick={() => run('list-backups', async () => {
                                const list = await loadBackups();
                                showNotice('ok', list.length ? `${list.length} respaldos en el bucket.` : 'No hay respaldos en el bucket.');
                            })}
                        >
                            {busy === 'list-backups' ? 'Cargando…' : 'Actualizar lista'}
                        </button>
                    </div>
                    {backupError && <p style={{ ...studio.hint, color: ink.danger }}>{backupError}</p>}
                    <select
                        value={selectedBackup}
                        onChange={(e) => setSelectedBackup(e.target.value)}
                        style={studio.control}
                        disabled={!backups.length}
                    >
                        <option value="">{backups.length ? '— Elige un respaldo —' : '— Sin respaldos —'}</option>
                        {backups.map((folder) => (
                            <option key={folder} value={folder}>{formatBackupLabel(folder)}</option>
                        ))}
                    </select>
                    {canRestore ? (
                        <button
                            type="button"
                            style={{ ...studio.dangerBtn, marginTop: 16 }}
                            disabled={disabled || !selectedBackup}
                            onClick={() => openConfirm({
                                title: 'Restaurar base de datos',
                                body: `Se sobreescribirá TODO Firestore con «${selectedBackup}». La aplicación puede dejar de responder mientras corre el import.`,
                                confirmLabel: 'Restaurar',
                                danger: true,
                                requireWord: 'RESTAURAR',
                                busyKey: 'restore',
                                action: async () => {
                                    const data = await api('/admin/maintenance/restore', {
                                        method: 'POST',
                                        body: JSON.stringify({ backupFolder: selectedBackup }),
                                    });
                                    showNotice('ok', data.message);
                                },
                            })}
                        >
                            {busy === 'restore' ? 'Restaurando…' : 'Restaurar respaldo seleccionado'}
                        </button>
                    ) : (
                        <p style={{ ...studio.hint, marginTop: 16 }}>
                            Restaurar solo lo puede un administrador del sistema o quien tenga el permiso «Restaurar base de datos».
                        </p>
                    )}
                </ActionCard>
            </Section>

            {confirm && (
                <div style={confirmUi.overlay} onClick={closeConfirm} role="presentation">
                    <div style={confirmUi.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={confirm.title}>
                        <p style={studio.kicker}>{confirm.danger ? 'Acción irreversible' : 'Confirmar'}</p>
                        <h3 style={{ ...studio.formTitle, margin: '6px 0 0', color: confirm.danger ? ink.danger : ink.text }}>{confirm.title}</h3>
                        <p style={{ ...studio.hint, marginTop: 10, lineHeight: 1.5, color: ink.muted }}>{confirm.body}</p>
                        {confirm.requireWord && (
                            <div style={{ margin: '16px 0 0' }}>
                                <Field label={`Escribe ${confirm.requireWord} para confirmar`}>
                                    <input
                                        value={typedConfirm}
                                        onChange={(e) => setTypedConfirm(e.target.value)}
                                        style={studio.control}
                                        autoComplete="off"
                                    />
                                </Field>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
                            <button type="button" onClick={closeConfirm} style={studio.ghostBtn}>Cancelar</button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={Boolean(confirm.requireWord && typedConfirm !== confirm.requireWord)}
                                style={{
                                    ...(confirm.danger ? studio.dangerBtn : studio.primaryBtn),
                                    opacity: confirm.requireWord && typedConfirm !== confirm.requireWord ? 0.5 : 1,
                                }}
                            >
                                {confirm.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function Section({ title, hint, children }) {
    return (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: ink.text }}>{title}</h3>
                <p style={{ ...studio.hint, marginTop: 4 }}>{hint}</p>
            </div>
            {children}
        </section>
    );
}

function ActionCard({ title, description, children }) {
    return (
        <div style={studio.card}>
            <h4 style={{ margin: '0 0 6px', fontSize: '0.98rem', fontWeight: 600, color: ink.accentText }}>{title}</h4>
            <p style={{ ...studio.hint, margin: 0, maxWidth: 640 }}>{description}</p>
            <div style={{ marginTop: 16 }}>{children}</div>
        </div>
    );
}

function DateRow({ start, end, onStart, onEnd }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: cols('1fr 1fr'), gap: 10, marginBottom: 10 }}>
            <Field label="Desde">
                <FilterDate value={start} onChange={onStart} title="Desde" />
            </Field>
            <Field label="Hasta">
                <FilterDate value={end} onChange={onEnd} title="Hasta" />
            </Field>
        </div>
    );
}

function ImpactLine({ children }) {
    return (
        <p style={{ fontSize: '0.9rem', color: ink.text, fontWeight: 600, margin: '0 0 14px' }}>
            Impacto: {children}
        </p>
    );
}

const checkStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    fontSize: '0.95rem',
    color: ink.text,
};

const confirmUi = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.45)',
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    sheet: {
        ...studio.card,
        width: 'min(480px, 100%)',
        boxShadow: '0 24px 64px rgba(28, 29, 33, 0.22)',
    },
};

export default MaintenancePanel;
